<script setup>
import { VForm } from 'vuetify/components/VForm'
import { useTrainerAccountStore } from '@/stores/trainerAccount'

definePage({
  meta: {
    layout: 'blank',
    allowOnboarding: true,
  },
})

const accountStore = useTrainerAccountStore()
const router = useRouter()
const { t } = useI18n()
const refVForm = ref()
const formError = ref('')
const statusMessage = ref('')

const TIMEZONES = [
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
]

const form = ref({
  name: '',
  business_name: '',
  timezone: 'Europe/Amsterdam',

  // Dutch is the only product UI language — always persist `nl` (design-system.md §2.6).
  locale: 'nl',
})

onMounted(async () => {
  if (!accountStore.trainer)
    await accountStore.bootstrap()

  if (accountStore.trainer) {
    form.value = {
      name: accountStore.trainer.name || '',
      business_name: accountStore.trainer.business_name || '',
      timezone: accountStore.trainer.timezone || 'Europe/Amsterdam',
      locale: 'nl',
    }
  }
})

const save = async () => {
  formError.value = ''
  statusMessage.value = ''
  try {
    const trainer = await accountStore.updateProfile(form.value)

    if (!trainer.confirmed) {
      statusMessage.value = t('onboarding.savedPendingConfirmation')
      
      return
    }

    if (trainer.lifecycle_state === 'active')
      await router.replace('/')
    else
      statusMessage.value = t('onboarding.saved')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || t('onboarding.genericError')
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      save()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="560"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <h1 class="text-h4 mb-1">
          {{ $t('onboarding.title') }}
        </h1>
        <p class="mb-4 text-medium-emphasis">
          {{ $t('onboarding.subtitle') }}
        </p>

        <VAlert
          color="info"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          <div class="text-subtitle-2 mb-1">
            {{ $t('onboarding.stateLabel') }}
          </div>
          <div>
            {{ accountStore.lifecycleLabel || $t('onboarding.defaultStatus') }}
          </div>
          <div
            v-if="accountStore.trainer && !accountStore.trainer.confirmed"
            class="mt-2"
          >
            {{ $t('onboarding.emailNotConfirmed') }}
            <RouterLink
              class="text-primary"
              :to="{ name: 'confirm-email', query: { email: accountStore.trainer.email } }"
            >
              {{ $t('onboarding.resendConfirmation') }}
            </RouterLink>
          </div>
        </VAlert>

        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VAlert
          v-if="statusMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ statusMessage }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                :label="$t('onboarding.nameLabel')"
                autocomplete="name"
                :rules="[requiredValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.business_name"
                :label="$t('onboarding.businessNameLabel')"
                :rules="[requiredValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VSelect
                v-model="form.timezone"
                :label="$t('common.timezone')"
                :items="TIMEZONES"
                :rules="[requiredValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                :loading="accountStore.loading"
              >
                {{ $t('onboarding.submit') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
